import PropTypes from "prop-types";
import styles from "./ProductCard.scss";
import Image from "../image/Image";
import SaveForLaterButton from "../buttons/saveForLaterButton/SaveForLaterButton";
import Spacing, { SPACING } from "../spacing/Spacing";
import ShowOnProductStockStatus from "../conditional/ShowOnProductStockStatus";
import OutOfStockLabel from "../outOfStockLabels/OutOfStockLabel";
import { SECONDARY_LABEL } from "../outOfStockLabels/labelTypes";
import SoldShippedHeader from "../deliveryAndReturns/soldShippedHeader/SoldShippedHeader";
import { TEXT_SIZES } from "../../constants";
import ProductColourForProductCard from "../productColour/ProductColourForProductCard";
import ProductSize from "../productSize/ProductSize";
import VariantSelectionContainer from "../../containers/variantSelection/VariantSelectionContainer";
import classnamesBind from "classnames/bind";
import { fireAfsAnalytics } from "../../analytics/pageInteraction";
import { closeModalAndScroll, isHeroProductWithoutLink } from "./utils";

const classnames = classnamesBind.bind(styles);

function getProductCardLink({
  product,
  productLink,
  onClick,
  children,
  dataTestId,
  className,
}) {
  if (isHeroProductWithoutLink({ product, productLink })) {
    return (
      <button
        onClick={(event) => {
          event.preventDefault();
          onClick?.(event);
        }}
        data-testid={dataTestId}
        className={className}
        type={"button"}
      >
        {children}
      </button>
    );
  }

  return (
    <a href={productLink} data-testid={dataTestId} className={className}>
      {children}
    </a>
  );
}

export default function ProductCard({
  product,
  productLink,
  handleSaveClick,
  variantIdentifier,
  fireAddToBagAnalytics,
  fireAddToBagErrorAnalytics,
  fireAddToBagMarketing,
  fireNotifyMeClickAnalytics,
  variantSelectorId,
  excludedFromSizeGuideExperiment,
  scope,
  imageWidth,
  primaryImageUrl,
  shouldShowProductControls = true,
  shouldRetainSmallImageSize,
  priceRenderProp,
  ratingRenderProp = () => null,
  labelsRenderProp = () => null,
  modalControls,
}) {
  return (
    <section className={styles.container} data-testid="productCard">
      <div className={styles.imageContainer}>
        {getProductCardLink({
          product,
          productLink,
          onClick: (event) => closeModalAndScroll({ modalControls, event }),
          children: (
            <Image
              url={primaryImageUrl}
              alternateText={product.name}
              type="card"
              width={imageWidth}
            />
          ),
          dataTestId: "productCardImageLink",
          className: classnames(styles.imageLink, {
            smallImageOnly: shouldRetainSmallImageSize,
          }),
        })}
        {shouldShowProductControls && (
          <SaveForLaterButton
            handleClick={handleSaveClick}
            productTitle={product.name}
            variantIdentifier={variantIdentifier}
            saveForLaterClass="looksSaveForLater"
          />
        )}
        <Spacing size={SPACING.EIGHT}>{labelsRenderProp()}</Spacing>
      </div>
      <div className={styles.aside}>
        <Spacing size={SPACING.EIGHT} excludeFirst={true}>
          <ShowOnProductStockStatus productStockStatus={false}>
            <OutOfStockLabel labelStyle={SECONDARY_LABEL} />
          </ShowOnProductStockStatus>
          {getProductCardLink({
            product,
            productLink,
            onClick: (event) => closeModalAndScroll({ modalControls, event }),
            dataTestId: "productCardTitleLink",
            className: styles.link,
            children: <h2 className={styles.title}>{product.name}</h2>,
          })}
          {priceRenderProp()}
          <SoldShippedHeader
            size={TEXT_SIZES.leavesden3}
            fireSellerInfoAnalytics={fireAfsAnalytics}
          />
          {ratingRenderProp()}
          <ProductColourForProductCard />
          <ProductSize size={TEXT_SIZES.leavesden3} />
          {!product?.isDiscontinued ? (
            <Spacing size={SPACING.TWELVE}>
              {shouldShowProductControls && (
                <VariantSelectionContainer
                  isInStock={product.isInStock}
                  shouldShowSave={false}
                  variantSelectorId={variantSelectorId}
                  excludedFromSizeGuideExperiment={excludedFromSizeGuideExperiment}
                  fireNotifyMeClickAnalytics={fireNotifyMeClickAnalytics}
                  fireAddToBagAnalytics={fireAddToBagAnalytics}
                  fireAddToBagErrorAnalytics={fireAddToBagErrorAnalytics}
                  fireAddToBagMarketing={fireAddToBagMarketing}
                  scope={scope}
                  selectedProductId={product.id}
                />
              )}
            </Spacing>
          ) : null}
        </Spacing>
      </div>
    </section>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    isDiscontinued: PropTypes.bool.isRequired,
    isInStock: PropTypes.bool.isRequired,
    shouldShowSave: PropTypes.bool.isRequired,
    images: PropTypes.arrayOf({
      isPrimary: PropTypes.bool.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    price: PropTypes.shape({
      current: PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.number,
      }).isRequired,
      previous: PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.number,
      }),
      rrp: PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.number,
      }),
      isMarkedDown: PropTypes.bool,
      isOutletPrice: PropTypes.bool,
      startDateTime: PropTypes.string,
    }).isRequired,
    id: PropTypes.number.isRequired,
    isHeroProduct: PropTypes.bool,
  }).isRequired,
  productLink: PropTypes.string,
  handleSaveClick: PropTypes.func.isRequired,
  variantIdentifier: PropTypes.string.isRequired,
  fireAddToBagAnalytics: PropTypes.func,
  fireAddToBagErrorAnalytics: PropTypes.func,
  fireAddToBagMarketing: PropTypes.func,
  fireNotifyMeClickAnalytics: PropTypes.func,
  variantSelectorId: PropTypes.string.isRequired,
  excludedFromSizeGuideExperiment: PropTypes.bool,
  scope: PropTypes.string,
  imageWidth: PropTypes.number.isRequired,
  primaryImageUrl: PropTypes.string.isRequired,
  shouldShowProductControls: PropTypes.bool,
  shouldRetainSmallImageSize: PropTypes.bool,
  priceRenderProp: PropTypes.func.isRequired,
  ratingRenderProp: PropTypes.func,
  labelsRenderProp: PropTypes.func,
  modalControls: PropTypes.shape({
    closeModal: PropTypes.func.isRequired,
    setFocusOnCloseElement: PropTypes.func.isRequired,
  }),
};
